import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Channel, ChannelNatsCredentials } from "@/types";

export interface CreateChannelPayload {
  // #185: type ist freier String (Plugin-Modell), UI bietet die bekannten an.
  type: string;
  name: string;
  config: Record<string, unknown>;
}

export interface CreateChannelResponse {
  channel: Channel;
  channelToken: string;
  // #185: NATS-Bundle für den Plugin-Container (1-Schritt-Onboarding).
  natsCredentials: ChannelNatsCredentials | null;
}

function useChannelsClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

function useInvalidateChannels() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.resource("channels") });
}

export function useCreateChannel(): UseMutationResult<
  CreateChannelResponse,
  Error,
  CreateChannelPayload
> {
  const getClient = useChannelsClient();
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: (input) => getClient().post<CreateChannelResponse>("/channels", input),
    onSuccess: invalidate,
  });
}

export function useUpdateChannel(): UseMutationResult<
  Channel,
  Error,
  { id: string; name?: string; config?: Record<string, unknown> }
> {
  const getClient = useChannelsClient();
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: ({ id, ...patch }) => getClient().patch<Channel>(`/channels/${id}`, patch),
    onSuccess: invalidate,
  });
}

export function useEnableChannel(): UseMutationResult<Channel, Error, string> {
  const getClient = useChannelsClient();
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: (id) => getClient().post<Channel>(`/channels/${id}/enable`),
    onSuccess: invalidate,
  });
}

export function useDisableChannel(): UseMutationResult<Channel, Error, string> {
  const getClient = useChannelsClient();
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: (id) => getClient().post<Channel>(`/channels/${id}/disable`),
    onSuccess: invalidate,
  });
}

export function useDeleteChannel(): UseMutationResult<void, Error, string> {
  const getClient = useChannelsClient();
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/channels/${id}`),
    onSuccess: invalidate,
  });
}

// Channel-Setup-Flow: Admin-Aktion (Button/Formular) → Master reicht 1:1 ans Plugin
// durch (202, fire-and-forget). Das Ergebnis kommt als neuer Setup-Push (Poll).
export interface ChannelSetupActionPayload {
  id: string;
  actionId: string;
  stepId?: string;
  values?: Record<string, unknown>;
}
export function useChannelSetupAction(): UseMutationResult<
  { ok: boolean },
  Error,
  ChannelSetupActionPayload
> {
  const getClient = useChannelsClient();
  const invalidate = useInvalidateChannels();
  return useMutation({
    mutationFn: ({ id, ...action }) =>
      getClient().post<{ ok: boolean }>(`/channels/${id}/setup/action`, action),
    onSuccess: invalidate,
  });
}

// #185 + #202: Rotate Credentials — admin-only. Gibt das KOMPLETTE neue Bundle
// zurück: frischer channel-token (alter sofort ungültig) + neues NATS-Bundle.
export interface RotateChannelCredentialsResponse {
  channelToken: string;
  natsCredentials: import("@/types").ChannelNatsCredentials | null;
}
export function useRotateChannelCredentials(): UseMutationResult<
  RotateChannelCredentialsResponse,
  Error,
  string
> {
  const getClient = useChannelsClient();
  return useMutation({
    mutationFn: (id) => getClient().post<RotateChannelCredentialsResponse>(
      `/channels/${id}/rotate-credentials`, {},
    ),
  });
}
